import { createContext } from 'react'
import { DateOfBirthValues } from '@zuto/molecules'
import QuestionKey from '../../Questions/question-key'
import { EventsServiceResult } from '../../services/events-service'
import { AddressHistoryState } from '../Questions/CardlessAddressHistoryQuestion/AddressHistoryContext'
import { EmploymentHistoryState } from '../Questions/CardlessEmploymentHistoryQuestion/EmploymentHistoryContext'
import MarketingPreferencesField from '../Questions/MarketingPreferencesField'
import { VehicleDetailsDataObject } from '../../services/vehicle-service'

export type UnitedPersonalDetailsAnswersType = {
  contactNumber: string
  dateOfBirth: DateOfBirthValues
  emailAddress: string
  firstName: string
  surname: string
  maritalStatus: string
  title: string
}

export type PersonalDetailsType = {
  title?: string
  firstName?: string
  surname?: string
  contactNumber?: string
  emailAddress?: string
  dateOfBirth?: DateOfBirthValues
}

export type ContactInfoValues = {
  contactNumber: string
  emailAddress: string
}

export type RequiredDetailsValues = {
  contactNumber?: string
  jobTitle?: string
}

export type MarketingPreferencesType = { email: boolean; sms: boolean }

export type lastNavigationAction = 'CONTINUE' | 'PREVIOUS' | 'IOS_BACK'

export type PrefillSectionType = 'edit-personal-details' | 'edit-vehicle-details' | 'edit-address-details' | 'edit-employment-details' | ''

export type Action =
  | {
      type: 'QUESTION_ANSWERED'
      name: string
      value: unknown
      errors?: { [key: string]: boolean }
      bouncerError?: string
    }
  | {
      type: 'SAVE'
      name: string
      value: unknown
      errors?: { [key: string]: boolean }
      bouncerError?: string
    }
  | {
      type: 'PREVIOUS_QUESTION'
    }
  | {
      type: 'SUBMIT'
      marketingPreferences: MarketingPreferencesField
    }
  | {
    type: 'CAN_PROCEED'
    value: boolean
  }
  | {
      type: 'SET_POST_CODE'
      value: string
    }
  | {
      type: 'SET_MARKETING_PREFERENCES'
      value: MarketingPreferencesType
    }
  | {
      type: 'EDIT_PREFILL_SECTION'
      value: PrefillSectionType
    }
  | {
      type: 'NAVIGATED_FROM_QUESTION'
      questionName: string
      lastNavAction?: lastNavigationAction
    }

export const featureSwitches = {
  addressRedesignV2: false,
  progressBarWithCar: false,
  resumeV1: false,
  removeContinueCtaV1: false,
  personalDetailsV4_2: false,
  reviewPageV3PreApproval: false,
  inputOpacity100_60: false,
  interstitialV1: false,
  isReapply: false,
  reviewPageV3: false,
  digiLandingPageV1: false,
  atLandingPageV4: false,
  inputIconsV1: false,
  isTrustpilotV1: false,
  questionCopyChangeV1: false,
  preInterstitialV1: false,
  atLandingPageV5: false,
  atLandingPageV6: false,
  firstTimeApplicantPreInterstitialV1: false,
  bouncerV1: false,
  requiredDetailsV1: false,
  vehicleImgV1: false,
  formlessJourneyV1: false,
  colorVariationV1: false,
  msmThemeV1: false,
  shouldRenderMSMInterstitial: false,
  formlessJourneyV3: false,
  formlessJourneyV4: false,
  marketingOptInV1: false,
  employerNotContactedV1: false
}

export type FeatureSwitches = Partial<typeof featureSwitches>



export type ApplicationFormContextState = {
  dispatch: (action: Action) => void
  getAppForm: () => ApplicationFormState
  featureSwitches?: FeatureSwitches
  eventsService?: EventsServiceResult
}

export const ApplicationFormContext = createContext<ApplicationFormContextState>({
  dispatch: () => {},
  getAppForm: () => ({}) as ApplicationFormState,
  featureSwitches: {}
})

export interface ApplicationFormAnswers {
  [QuestionKey.VEHICLE_TYPE]?: string
  [QuestionKey.LOAN_AMOUNT]?: string
  [QuestionKey.MONTHLY_INCOME]?: string | number
  [QuestionKey.DRIVING_LICENCE]?: string
  [QuestionKey.MARITAL_STATUS]?: string
  [QuestionKey.EMPLOYMENT_HISTORY]?: EmploymentHistoryState
  [QuestionKey.ADDRESS_HISTORY]?: AddressHistoryState
  [QuestionKey.PERSONAL_DETAILS]?: PersonalDetailsType
  [QuestionKey.DATE_OF_BIRTH]?: DateOfBirthValues
  [QuestionKey.CONTACT_INFO]?: ContactInfoValues
  [QuestionKey.REQUIRED_DETAILS]?: RequiredDetailsValues
  partnerCustomerId?: string
  vehicleDetails?: VehicleDetailsDataObject
}

export type ApplicationFormState = {
  currentQuestionIndex: number
  answers: ApplicationFormAnswers
  furthestQuestionIndex: number
  canProceed: boolean
  postalCode: string
  marketingPreferences: MarketingPreferencesType
  lastNavigationAction?: lastNavigationAction
  allErrors: { [key: string]: { [key: string]: boolean } }
  prefillSection?: PrefillSectionType
  questionName?: string
  lastNavAction?: lastNavigationAction
  bouncerError?: string
}

export interface LenderDetails {
  questionSet?: QuestionKey[]
  lenderName?: string
  apr?: number
  preapproved?: boolean
  route?: string
  quoteId?: string
}
